import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/auth';
import { useAuthRedirect } from './useAuthRedirect';
import { useCompanyId } from './useCompanyId';
import { isCompanyAdmin, isOperatorStaff } from '../utils/permission';
import { isApiAccessActive, toApiStartDate } from '../utils/apiFeature';
import { getCompany } from '../api/firebase/firestore/company/read';

export interface UseApiPageAccessOptions {
  /**
   * 運営スタッフが自身の所属企業を持たない場合に閲覧を許可するか
   * - APIキー管理画面: false（必ず企業を指定する必要がある）
   * - APIドキュメント画面: true（運営スタッフは事前確認のため常時閲覧可）
   */
  allowOperatorWithoutCompany?: boolean;
}

export interface UseApiPageAccessReturn {
  /** 認証 or 企業情報のロード中 */
  loading: boolean;
  /** 企業情報取得などで失敗した場合のエラーメッセージ */
  errorText: string;
  /** 対象企業で API 機能が有効化されているか */
  apiEnabled: boolean;
  /** API 機能の利用開始日（未設定なら null） */
  apiStartDate: Date | null;
  /** 開始日を「YYYY年M月D日」形式で整形した文字列 */
  startDateText: string;
  /** 画面コンテンツを表示してよいか（apiEnabled かつ開始日到達 or 運営閲覧） */
  canAccess: boolean;
  /** 対象企業 ID（代理モード時は対象企業、それ以外は自社） */
  effectiveCompanyId: string | null;
  /** 運営スタッフ単独セッション（自身に紐づく企業情報が無い） */
  isOperatorWithoutCompany: boolean;
}

/**
 * API 機能を持つ画面（APIキー管理 / API ドキュメント）の共通アクセス制御フック
 *
 * - 未ログイン → `/` に遷移
 * - companyAdmin / operatorStaff 以外 → `/home` に遷移
 * - 対象企業の `api_enabled` と `api_start_date` を取得して canAccess を判定
 *
 * 顧客側ビュー（companyAdmin or 代理モード）は利用開始日に到達するまで `canAccess=false`。
 * 運営スタッフ自身のセッションは事前準備のため常時 `canAccess=true`。
 */
export const useApiPageAccess = (options: UseApiPageAccessOptions = {}): UseApiPageAccessReturn => {
  const { allowOperatorWithoutCompany = false } = options;
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, isLoading: authLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();

  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [apiEnabled, setApiEnabled] = useState(false);
  const [apiStartDate, setApiStartDate] = useState<Date | null>(null);
  const [isOperatorWithoutCompany, setIsOperatorWithoutCompany] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let active = true;
    const run = async (): Promise<void> => {
      if (!user) {
        return;
      }
      if (!isCompanyAdmin(user.role) && !isOperatorStaff(user.role)) {
        navigate('/home');
        return;
      }

      const targetCompanyId = effectiveCompanyId ?? user.company_id;
      if (!targetCompanyId) {
        if (allowOperatorWithoutCompany && isOperatorStaff(user.role)) {
          setIsOperatorWithoutCompany(true);
          setApiEnabled(true);
          setLoading(false);
          return;
        }
        setErrorText('企業情報が取得できませんでした。');
        setLoading(false);
        return;
      }

      try {
        const company = await getCompany(targetCompanyId);
        if (!active) return;
        if (!company) {
          setErrorText('企業情報が取得できませんでした。');
        } else {
          setApiEnabled(company.api_enabled === true);
          setApiStartDate(toApiStartDate(company.api_start_date));
        }
      } catch (e) {
        if (!active) return;
        setErrorText(e instanceof Error ? e.message : '企業情報の取得に失敗しました。');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [authLoading, user, navigate, effectiveCompanyId, allowOperatorWithoutCompany]);

  // 代理モード or 企業管理者は開始日に到達するまでアクセス不可。
  // 運営スタッフ自身のセッションは事前準備のため常時アクセス可。
  const isOperator = isOperatorStaff(user?.role);
  const apiActive = isApiAccessActive(apiEnabled, apiStartDate);
  const isCustomerView = isCompanyAdmin(user?.role) || isProxyMode;
  const canAccess = apiEnabled && (isCustomerView ? apiActive : isOperator);
  const startDateText = apiStartDate
    ? `${apiStartDate.getFullYear()}年${apiStartDate.getMonth() + 1}月${apiStartDate.getDate()}日`
    : '';

  return {
    loading: loading || authLoading,
    errorText,
    apiEnabled,
    apiStartDate,
    startDateText,
    canAccess,
    effectiveCompanyId,
    isOperatorWithoutCompany,
  };
};
